import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { RuntimeConfigService } from './runtime-config.service';


@Injectable()
export class GlobalRequestManagerService {
  private killPreviousPageRequests$ = new Subject<void>();
  private activeRequests = new Map<string, any>();
  private requestCounter = 0;
  private isBlockingRequests = false;
  private pendingRequests: Array<{resolve: Function, url: string}> = [];

  constructor(
    private router: Router,
    private runtimeConfigService: RuntimeConfigService
  ) {
    this.setupNavigationKiller();
    this.setupPageRefreshKiller();
  }

  // Check whether an endpoint is safe to keep running during navigation/refresh.
  private isProtectedEndpoint(url: string): boolean {
    if (!url) {
      return false;
    }

    const normalizeUrl = (u: string) => {
      if (!u) {
        return '';
      }
      return u.replace(/^\/+|\/+$/g, '').toLowerCase();
    };

    const normalizedUrl = normalizeUrl(url);
    const protectedApiEndpoints = this.runtimeConfigService.getProtectedApiEndpoints();

    return protectedApiEndpoints.some(protectedUrl => {
      if (!protectedUrl) {
        return false;
      }
      const normalizedProtected = normalizeUrl(protectedUrl);
      if (normalizedUrl === normalizedProtected) {
        return true;
      }
      if (normalizedUrl.includes(normalizedProtected) || normalizedProtected.includes(normalizedUrl)) {
        return true;
      }
      return false;
    });
  }

  // Cancel non-protected in-flight requests when route navigation starts.
  private setupNavigationKiller(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isBlockingRequests = true;

        if (this.activeRequests.size > 0) {
          let hasCancelled = false;
          this.activeRequests.forEach((req, id) => {
            if (!this.isProtectedEndpoint(req.url)) {
              console.log('[RequestManager] Cancelled on navigation: ' + req.url);
              this.activeRequests.delete(id);
              hasCancelled = true;
            }
          });
          if (hasCancelled) {
            this.killPreviousPageRequests$.next();
          }
        }

        setTimeout(() => {
          this.isBlockingRequests = false;
          if (this.pendingRequests.length > 0) {
            this.pendingRequests.forEach(req => req.resolve());
            this.pendingRequests = [];
          }
        }, 100);
      }
    });
  }

  // Cancel non-protected in-flight requests when the user refreshes/leaves the page.
  private setupPageRefreshKiller(): void {
    const protectedRequestsData = sessionStorage.getItem('protectedRequestsOnRefresh');
    if (protectedRequestsData) {
      try {
        JSON.parse(protectedRequestsData);
        sessionStorage.removeItem('protectedRequestsOnRefresh');
      } catch (e) {
      }
    }

    const lastRefreshData = sessionStorage.getItem('lastRefreshCancellation');
    if (lastRefreshData) {
      try {
        const data = JSON.parse(lastRefreshData);
        if (data.requests && data.requests.length > 0) {
          console.log('[RequestManager] ' + data.requests.length + ' request(s) were cancelled on last page refresh:');
          data.requests.forEach((req: any) => {
            console.log('[RequestManager]   - ' + req.url + ' (was running for ' + req.duration + 'ms)');
          });
        }
        sessionStorage.removeItem('lastRefreshCancellation');
      } catch (e) {
      }
    }

    window.addEventListener('beforeunload', () => {
      if (this.activeRequests.size === 0) {
        return;
      }

      const protectedRequests: any[] = [];
      const cancelledRequests: any[] = [];

      this.activeRequests.forEach((req, id) => {
        const runningTime = Date.now() - req.startTime;
        if (this.isProtectedEndpoint(req.url)) {
          protectedRequests.push({ id: id, req: req });
        } else {
          cancelledRequests.push({
            url: req.url,
            duration: runningTime
          });
          console.log('[RequestManager] Cancelled on refresh: ' + req.url + ' (was running for ' + runningTime + 'ms)');
        }
      });

      if (cancelledRequests.length > 0) {
        sessionStorage.setItem('lastRefreshCancellation', JSON.stringify({
          count: cancelledRequests.length,
          timestamp: Date.now(),
          requests: cancelledRequests
        }));
        this.killPreviousPageRequests$.next();
        this.activeRequests.forEach((req, id) => {
          if (!this.isProtectedEndpoint(req.url)) {
            this.activeRequests.delete(id);
          }
        });
      }

      if (protectedRequests.length > 0) {
        const protectedRequestsInfo = protectedRequests.map((item) => ({
          url: item.req.url,
          duration: Date.now() - item.req.startTime,
          startTime: item.req.startTime
        }));
        sessionStorage.setItem('protectedRequestsOnRefresh', JSON.stringify({
          count: protectedRequests.length,
          timestamp: Date.now(),
          requests: protectedRequestsInfo
        }));
      }

      this.pendingRequests = [];
      this.isBlockingRequests = false;
    });
  }

  // Wrap an HTTP observable so it can be cancelled during navigation (unless protected).
  public wrapRequest<T>(observable: Observable<T>, url: string): Observable<T> {
    if (this.isBlockingRequests) {
      return new Observable<T>(observer => {
        const promise = new Promise<void>((resolve) => {
          this.pendingRequests.push({ resolve: resolve, url: url });
        });
        promise.then(() => {
          this.executeRequest(observable, url).subscribe(
            data => observer.next(data),
            error => observer.error(error),
            () => observer.complete()
          );
        });
        return () => {};
      });
    }
    return this.executeRequest(observable, url);
  }

  // Execute and track the request; apply takeUntil cancellation only for non-protected endpoints.
  private executeRequest<T>(observable: Observable<T>, url: string): Observable<T> {
    const requestId = 'req_' + (++this.requestCounter);
    const startTime = Date.now();
    const isProtected = this.isProtectedEndpoint(url);

    this.activeRequests.set(requestId, { url: url, startTime: startTime, isProtected: isProtected });

    return new Observable<T>(observer => {
      let source$ = observable;
      if (!isProtected) {
        source$ = observable.pipe(takeUntil(this.killPreviousPageRequests$));
      }

      let settled = false;
      const subscription = source$
        .pipe(
          finalize(() => {
            this.activeRequests.delete(requestId);
          })
        )
        .subscribe(
          data => {
            if (!settled) {
              settled = true;
              observer.next(data);
              observer.complete();
            }
          },
          error => {
            if (!settled) {
              settled = true;
              observer.error(error);
            }
          },
          () => {
            if (!settled) {
              settled = true;
              observer.complete();
            }
          }
        );

      return () => {
        this.activeRequests.delete(requestId);
        subscription.unsubscribe();
      };
    });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src947738962/src/src/app/shared/services/global-request-manager.service.ts